// src/pages/part-labor-mapping/index.jsx
import React, { useMemo, useRef, useState } from "react";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";

import { useLanguage } from "../../../contexts/LanguageContext";
import {
  useGetLaborsQuery,
  useGetPartsQuery,
} from "../../../api/endpoints/partLaborMapping";

import GenericTableFilter from "../../../components/common/GenericTableFilter";
import { createInitialFilters } from "../../../utils/filterUtils";
import EditPartLaborsDialog from "./components/EditPartLaborsDialog";

const PartLaborMappingPage = () => {
  const { th, getCurrentLanguageId } = useLanguage();
  const toast = useRef(null);

  const [lazyParams, setLazyParams] = useState({
    first: 0,
    rows: 10,
    page: 1,
    sortField: null,
    sortOrder: null,
  });

  const [activeFilters, setActiveFilters] = useState({
    partCode: "",
    partName: "",
  });

  const [editOpen, setEditOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const langId = getCurrentLanguageId() ?? 2;

  const {
    data: partsResp,
    isLoading: loading,
    refetch: refetchParts,
  } = useGetPartsQuery(
    {
      languageId: langId,
      pageNumber: lazyParams.page,
      pageSize: lazyParams.rows,
      partCode: activeFilters.partCode || undefined
    },
    { refetchOnMountOrArgChange: true }
  );

  const parts = partsResp?.data?.items ?? partsResp?.data?.Items ?? [];
  const totalRecords =
    partsResp?.data?.totalCount ?? partsResp?.data?.TotalCount ?? 0;

  const { data: laborsResp } = useGetLaborsQuery({ languageId: langId });
  const labors = laborsResp?.data ?? [];

  const sortedData = useMemo(() => {
    if (!lazyParams.sortField) return parts;

    return [...parts].sort((a, b) => {
      const aVal = a?.[lazyParams.sortField] ?? "";
      const bVal = b?.[lazyParams.sortField] ?? "";
      const order = lazyParams.sortOrder === 1 ? 1 : -1;
      return (
        order *
        aVal.toString().localeCompare(bVal.toString(), "tr", { numeric: true })
      );
    });
  }, [parts, lazyParams.sortField, lazyParams.sortOrder]);

  const [filters, setFilters] = useState(() =>
    createInitialFilters(["partCode", "partName"], {
      partCode: "text"
    })
  );

  const filterFields = useMemo(
    () => [
      { field: "partCode", headerName: th("product.code"), type: "text" }
    ],
    [th]
  );

  const handleAdvancedFilterChange = (updated) => {
    const apiFilters = {
      partCode: updated.partCode ? updated.partCode.toUpperCase() : ""
    };

    setActiveFilters(apiFilters);

    setLazyParams((prev) => ({
      ...prev,
      first: 0,
      page: 1,
    }));
  };

  const initFilters = () => {
    setActiveFilters({
      partCode: ""
    });

    setLazyParams((prev) => ({
      ...prev,
      first: 0,
      page: 1,
      sortField: null,
      sortOrder: null,
    }));
  };

  const onPage = (event) => {
    const calculatedPage = Math.floor(event.first / event.rows) + 1;
    setLazyParams((prev) => ({
      ...prev,
      first: event.first,
      rows: event.rows,
      page: calculatedPage,
    }));
  };

  /** ---------- Sort ---------- */
  const onSort = (event) => {
    setLazyParams((prev) => ({
      ...prev,
      sortField: event.sortField,
      sortOrder: event.sortOrder,
    }));
  };

  const mappedCount = (row) => row?.laborMappings?.length ?? row?.LaborMappings?.length ?? 0;

  const editTemplate = (row) => (
    <Button
      icon="pi pi-pencil"
      label={th("common.edit")}
      onClick={() => {
        // Dialog'un beklediği PascalCase uyumluluğu
        const compat = {
          ...row,
          PartCode: row.partCode ?? row.PartCode,
          PartName: row.partName ?? row.PartName,
          LaborMappings: row.laborMappings ?? row.LaborMappings,
        };
        setSelectedRow(compat);
        setEditOpen(true);
      }}
    />
  );

  return (
    <div className="card">
      <Toast ref={toast} />

      <div className="flex justify-content-between align-items-center mb-4">
        <h1 className="m-0">{th("parts.titlePartLaborMapping")}</h1>
      </div>

      <div className="mb-2">
        <GenericTableFilter
          filterFields={filterFields}
          initialFilters={{}}
          onSearch={(text) => {
            const apiFilters = {
              partCode: text ? text.toUpperCase() : ""
            };
            setActiveFilters(apiFilters);
            setLazyParams((prev) => ({ ...prev, first: 0, page: 1 }));
          }}
          onApplyFilters={handleAdvancedFilterChange}
          onResetFilters={initFilters}
        />
      </div>

      <DataTable
        value={sortedData}
        lazy
        paginator
        first={lazyParams.first}
        rows={lazyParams.rows}
        totalRecords={totalRecords}
        onPage={onPage}
        onSort={onSort}
        sortField={lazyParams.sortField}
        sortOrder={lazyParams.sortOrder}
        className="p-datatable-gridlines"
        showGridlines
        dataKey="partCode"
        loading={loading}
        emptyMessage={th("parts.emptyMessage")}
        rowsPerPageOptions={[5, 10, 25, 50]}
        tableStyle={{ minWidth: "50rem" }}
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        currentPageReportTemplate={th("table.paginatorTemplate")}
        stripedRows
        responsiveLayout="scroll"
      >
        <Column
          body={editTemplate}
          style={{ width: "5%" }}
          align="center"
          header={th("laborCatalog.table.actions")}
        />
        <Column
          field="partCode"
          header={th("product.code")}
          sortable
          style={{ width: "20%" }}
        />
        <Column
          field="partName"
          header={th("product.name")}
          sortable
        />
        <Column
          header={th("parts.mappedCount")}
          body={mappedCount}
          sortable
          style={{ width: "18%" }}
        />
      </DataTable>

      <EditPartLaborsDialog
        visible={editOpen}
        onHide={() => setEditOpen(false)}
        partRow={selectedRow}
        labors={labors}
        onSaved={() => {
          // Kaydet/Sil sonrası listeyi yenile
          if (lazyParams.page !== 1) {
            setLazyParams((p) => ({ ...p, first: 0, page: 1 }));
          } else {
            refetchParts();
          }
        }}
      />
    </div>
  );
};

export default PartLaborMappingPage;
